import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { QRCodeCanvas } from "qrcode.react";
import { toast } from "sonner";
import { ArrowLeft, ArrowSquareOut, DownloadSimple, Storefront, Tag, Copy, PencilSimple, EnvelopeSimple, CurrencyDollar } from "@phosphor-icons/react";
import api, { formatApiError } from "../api";
import { BrutalButton, BrutalInput } from "../components/Brutal";
import { composeCardPng, saveCardPng } from "../components/CodeCardDialog";
import { copyToClipboard } from "../lib/clipboard";
import { useAuth } from "../AuthContext";

/**
 * v406.1 — Stores directory. Owner/admin/cfo see every distributor storefront
 * in the org (partners: their subtree) — open it, grab the QR, or god-mode
 * manage its retail prices. No codes to remember.
 */
export default function StoresPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, isSuperOwner } = useAuth();
  const qrRef = useRef(null);
  const [stores, setStores] = useState(null);
  const [qrTarget, setQrTarget] = useState(null); // store being rendered for QR download
  const [editTarget, setEditTarget] = useState(null); // v406.11 code/region dialog
  const [busyId, setBusyId] = useState(null);

  const canGod = ["owner", "admin"].includes(user?.role) || isSuperOwner;

  const load = () =>
    api.get("/stores")
      .then(({ data }) => setStores(data.stores))
      .catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);

  const sendWalkthrough = async (s) => {
    if (!window.confirm(t("stores.walkthrough_confirm", { name: s.name }))) return;
    setBusyId(s.id);
    try {
      const { data } = await api.post(`/stores/${s.id}/send-walkthrough`);
      toast.success(t("stores.walkthrough_sent", { emails: data.sent_to.join(", ") }));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyId(null);
    }
  };

  const urlFor = (s) => `${window.location.origin}${s.store_path}`;

  const downloadQr = (s) => {
    setQrTarget(s);
    // let the hidden canvas re-render with the new value first
    setTimeout(async () => {
      const png = composeCardPng(qrRef.current?.querySelector("canvas"), {
        name: s.name,
        code: s.code,
        heading: t("code_card.store_heading"),
        instruction: t("code_card.store_instruction"),
        url: urlFor(s),
        tagline: t("code_card.tagline_default"),
      });
      const how = await saveCardPng(png, `${s.code}-store-qr.png`, urlFor(s));
      if (how === "downloaded") toast.success(t("code_card.downloaded"));
      if (how === "shared") toast.success(t("code_card.share_saved"));
    }, 120);
  };

  if (!stores) {
    return <div className="p-8 text-sm text-zinc-500" data-testid="stores-loading">{t("common.loading")}</div>;
  }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 space-y-5" data-testid="stores-page">
      {qrTarget && (
        <div ref={qrRef} className="hidden">
          <QRCodeCanvas value={urlFor(qrTarget)} size={480} fgColor="#09090b" level="M" includeMargin />
        </div>
      )}

      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <Link to="/" className="inline-flex items-center gap-1 text-xs font-medium text-zinc-500 hover:text-zinc-800 mb-1">
            <ArrowLeft size={12} weight="bold" /> {t("common.back")}
          </Link>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight flex items-center gap-2">
            <Storefront size={26} weight="bold" className="text-[#8a6a1c]" /> {t("stores.title")}
          </h1>
          <p className="text-sm text-zinc-600 mt-0.5">{t("stores.subtitle")}</p>
        </div>
        {canGod && (
          <BrutalButton size="sm" variant="secondary" onClick={() => navigate("/price-sheets")} data-testid="open-price-sheets-btn">
            <CurrencyDollar size={14} weight="bold" /> {t("stores.price_sheets_btn")}
          </BrutalButton>
        )}
      </div>

      {stores.length === 0 ? (
        <div className="bg-white border border-zinc-200 rounded-xl p-6 text-center" data-testid="stores-empty">
          <p className="text-sm font-medium text-zinc-900">{t("stores.empty")}</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {stores.map((s) => (
            <div key={s.id} data-testid={`store-card-${s.code}`}
              className="bg-white border border-zinc-200 rounded-xl p-4 flex flex-col gap-3 hover:border-[#c9a24a] transition-colors">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono font-bold text-[13px] bg-[#fbf6e7] border border-[#c9a24a]/60 rounded-md px-2 py-0.5">{s.code}</span>
                  {s.role === "partner" && (
                    <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full border bg-zinc-100 text-zinc-600 border-zinc-200">{t("stores.partner")}</span>
                  )}
                  {s.region && (
                    <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full border bg-sky-50 text-sky-700 border-sky-200">{s.region}</span>
                  )}
                </div>
                <p className="text-sm font-semibold text-zinc-900 mt-1.5 truncate">{s.name}</p>
                <p className="text-[11px] text-zinc-500 mt-0.5">
                  {t("stores.overrides", { n: s.overrides_count })}
                  <span className="mx-1.5 text-zinc-300">·</span>
                  {t("stores.orders_line", { awaiting: s.orders_awaiting, paid: s.orders_paid })}
                  {s.setup_total > 0 && (
                    <>
                      <span className="mx-1.5 text-zinc-300">·</span>
                      <span className={s.setup_done >= s.setup_total ? "text-emerald-600 font-semibold" : "text-amber-600 font-semibold"}
                        data-testid={`store-setup-${s.code}`}>
                        {t("stores.setup", { done: s.setup_done, total: s.setup_total })}
                      </span>
                    </>
                  )}
                </p>
                <button
                  type="button"
                  onClick={() => copyToClipboard(urlFor(s), t("my_store.link_copied"))}
                  className="mt-1 inline-flex items-center gap-1 font-mono text-[11px] text-[#8a6a1c] hover:underline truncate max-w-full"
                  data-testid={`store-copy-link-${s.code}`}
                  title={t("my_store.copy")}
                >
                  <Copy size={11} weight="bold" className="shrink-0" />
                  <span className="truncate">{urlFor(s).replace(/^https?:\/\//, "")}</span>
                </button>
              </div>
              <div className="flex gap-2 mt-auto flex-wrap">
                <BrutalButton size="sm" className="flex-1" onClick={() => window.open(urlFor(s), "_blank")}
                  data-testid={`store-open-${s.code}`}>
                  <ArrowSquareOut size={13} weight="bold" /> {t("stores.open")}
                </BrutalButton>
                <BrutalButton size="sm" variant="secondary" className="flex-1"
                  onClick={() => navigate(`/my-store?as=${s.id}`)} data-testid={`store-manage-${s.code}`}>
                  <Tag size={13} weight="bold" /> {t("stores.manage")}
                </BrutalButton>
                <BrutalButton size="sm" variant="secondary" onClick={() => downloadQr(s)}
                  data-testid={`store-qr-${s.code}`} title={t("my_store.download_qr")}>
                  <DownloadSimple size={13} weight="bold" />
                </BrutalButton>
                {canGod && (
                  <>
                    <BrutalButton size="sm" variant="secondary" onClick={() => setEditTarget(s)}
                      data-testid={`store-identity-edit-${s.code}`} title={t("stores.edit_identity")}>
                      <PencilSimple size={13} weight="bold" />
                    </BrutalButton>
                    <BrutalButton size="sm" variant="secondary" disabled={busyId === s.id}
                      onClick={() => sendWalkthrough(s)}
                      data-testid={`store-walkthrough-${s.code}`} title={t("stores.send_walkthrough")}>
                      <EnvelopeSimple size={13} weight="bold" />
                    </BrutalButton>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {editTarget && (
        <EditIdentityDialog
          store={editTarget}
          onClose={() => setEditTarget(null)}
          onSaved={() => { setEditTarget(null); load(); }}
          t={t}
        />
      )}
    </div>
  );
}

function EditIdentityDialog({ store, onClose, onSaved, t }) {
  const [name, setName] = useState(store.name || "");
  const [code, setCode] = useState(store.code);
  const [region, setRegion] = useState(store.region || "");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.patch(`/stores/${store.id}/identity`, {
        name: name.trim(),
        distributor_code: code.trim().toUpperCase(),
        region,
      });
      toast.success(data.changes?.distributor_code
        ? t("stores.identity_saved_code", { code: data.changes.distributor_code.to })
        : t("common.saved"));
      onSaved();
    } catch (e2) {
      toast.error(formatApiError(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} data-testid="store-identity-dialog"
        className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-5 space-y-3">
        <h2 className="font-semibold text-lg">{t("stores.edit_identity")}</h2>
        <p className="text-xs text-zinc-500">{store.name}</p>
        <div>
          <label className="text-xs font-medium text-zinc-600">{t("stores.name_label")}</label>
          <BrutalInput value={name} onChange={(e) => setName(e.target.value)}
            maxLength={60} data-testid="identity-name-input" />
          <p className="text-[10px] text-zinc-400 mt-0.5">{t("stores.name_hint")}</p>
        </div>
        <div>
          <label className="text-xs font-medium text-zinc-600">{t("stores.code_label")}</label>
          <BrutalInput value={code} onChange={(e) => setCode(e.target.value.toUpperCase())}
            className="font-mono tracking-widest" maxLength={12} data-testid="identity-code-input" />
          <p className="text-[10px] text-zinc-400 mt-0.5">{t("stores.code_hint")}</p>
        </div>
        <div>
          <label className="text-xs font-medium text-zinc-600">{t("stores.region_label")}</label>
          <BrutalInput value={region} onChange={(e) => setRegion(e.target.value)}
            placeholder="Ontario / Alberta / …" data-testid="identity-region-input" />
          <p className="text-[10px] text-zinc-400 mt-0.5">{t("stores.region_hint")}</p>
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <BrutalButton type="button" variant="secondary" onClick={onClose} data-testid="identity-cancel">
            {t("common.cancel")}
          </BrutalButton>
          <BrutalButton type="submit" disabled={busy} data-testid="identity-save">
            {busy ? t("common.saving") : t("common.save")}
          </BrutalButton>
        </div>
      </form>
    </div>
  );
}
